import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';

import { DateTime } from 'luxon';
import $ from 'jquery';

import { useTitle } from '@admin/hooks/use_title';
import Dashboard from '@admin/react/components/DispatchLiveDashboard/DashboardLayout';

import { loadGoogleMapsApi, resetGoogleMapsApi } from './google_maps';
import { createOpsMapStore } from './store';
import { useOpsMapUrlState } from './url_state';
import { useOrderRedirect } from './use_order_redirect';

const MAPS_LOAD_ERROR_MESSAGE = 'Unable to load Google Maps. Please try again.';

type MapsLoadState = 'loading' | 'ready' | 'error';

export const OpsMap: React.FC = () => {
  useTitle('Ops Dashboard');
  const store = useMemo(() => createOpsMapStore(), []);
  const user = window.clutter.user;
  const { params, go, ready: urlReady } = useOpsMapUrlState(user.region_ids ?? []);
  const [mapsLoadState, setMapsLoadState] = useState<MapsLoadState>('loading');

  useOrderRedirect({ ready: urlReady, params, go });

  const loadMaps = useCallback(() => {
    setMapsLoadState('loading');
    loadGoogleMapsApi()
      .then(() => setMapsLoadState('ready'))
      .catch(() => setMapsLoadState('error'));
  }, []);

  useEffect(() => {
    loadMaps();
  }, [loadMaps]);

  useEffect(() => {
    $('body').addClass('single-view-app');
    return () => {
      $('body').removeClass('single-view-app');
    };
  }, []);

  if (!urlReady || mapsLoadState === 'loading') {
    return null;
  }

  if (mapsLoadState === 'error') {
    return (
      <div className="alert alert-danger" role="alert">
        {MAPS_LOAD_ERROR_MESSAGE}{' '}
        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={() => {
            resetGoogleMapsApi();
            loadMaps();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const date = params.date ? DateTime.fromISO(params.date).toJSDate() : null;

  return (
    <Provider store={store}>
      <Dashboard
        go={go}
        date={date}
        regionId={params.region !== undefined ? Number(params.region) : undefined}
        activeCheckinId={params.checkin !== undefined ? Number(params.checkin) : undefined}
        activeDispatchId={params.dispatch !== undefined ? Number(params.dispatch) : undefined}
        activeOrderId={params.order !== undefined ? Number(params.order) : undefined}
      />
    </Provider>
  );
};
